/**
 * Nutrition foods API client using oRPC.
 */

import { Result } from "true-myth/result";

import type {
  ImportFoodsRequest,
  ImportFoodsResponse,
  ListFoodsRequest,
  ListFoodsResponse,
  NutritionFoodsApiError,
} from "@animus/api-treaty";

import type { AuthStrategy } from "../../config/api-config";
import { callApi, createApiClient } from "../../config/orpc-client";

export function createFoodsApi(baseUrl: string, auth: AuthStrategy) {
  const api = createApiClient(baseUrl, auth);

  async function listFoods(
    options?: ListFoodsRequest
  ): Promise<Result<ListFoodsResponse, NutritionFoodsApiError>> {
    const result = await callApi(
      api.nutritionFoods.list({
        limit: options?.limit,
        offset: options?.offset,
        search: options?.search,
        foodGroup: options?.foodGroup,
        tags: options?.tags,
      }),
      "Failed to fetch foods"
    );

    if (result.isErr) {
      return Result.err({
        code: result.error.code ?? "unknown",
        message: result.error.message,
      });
    }

    return Result.ok(result.value);
  }

  async function importFoods(
    request: ImportFoodsRequest
  ): Promise<Result<ImportFoodsResponse, NutritionFoodsApiError>> {
    const result = await callApi(
      api.nutritionFoods.import(request),
      "Failed to import foods"
    );

    if (result.isErr) {
      return Result.err({
        code: result.error.code ?? "unknown",
        message: result.error.message,
      });
    }

    return Result.ok(result.value);
  }

  return {
    listFoods,
    importFoods,
  };
}

export type FoodsApi = ReturnType<typeof createFoodsApi>;
